import { useState, useEffect, useRef } from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Trash2 } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Form } from '@/components/ui/form';
import SpinnerButton from '@/components/common/SpinnerButton';
import Input from '@/components/form/Input';
import Error from '@/components/common/Error';
import Spinner from '@/components/common/Spinner';

import { useDeleteDocument } from '@/data/document';

import Document from '@/app/document/Document';
import DocumentUploader from '@/app/document/DocumentUploader';
import { DOCUMENT_TYPE } from '@/app/document/enums';
import {
  useListCertifications,
  usePutCertification,
  useDeleteCertification
} from '@/data/certification';

const formSchema = z.object({
  name: z.string().min(1, { message: 'Required' })
});

const Certification = ({
  certification,
  listObj,
  placeholder,
  onDirtyChange
}) => {
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const putCertification = usePutCertification();
  const certifications = useListCertifications(listObj);
  const deleteDocument = useDeleteDocument();
  const deleteCertification = useDeleteCertification();

  const isShowDelete = certifications.data.length > 1;

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: certification.name || ''
    }
  });

  const { isDirty } = form.formState;

  const prevDirtyRef = useRef(isDirty);

  useEffect(() => {
    if (prevDirtyRef.current !== isDirty) {
      onDirtyChange?.({ certificationId: certification.id, isDirty });
      prevDirtyRef.current = isDirty;
    }
  }, [isDirty, onDirtyChange, certification.id]);

  const onSubmit = async (values) => {
    setSubmitting(true);
    const payload = {
      name: values.name
    };
    try {
      setApiError('');
      const updatedCertification = await putCertification(
        certification.id,
        payload
      );
      setSubmitting(false);
      certifications.mutate((currentCertifications) => {
        return currentCertifications.map((cert) =>
          cert.id === certification.id ? updatedCertification : cert
        );
      }, false);
      form.reset(values);
    } catch (err) {
      setApiError(err.message);
      setSubmitting(false);
    }
  };

  const handleDeleteDocument = async (id) => {
    try {
      setApiError('');
      await deleteDocument(id);
      certifications.mutate();
    } catch (err) {
      setApiError(err.message);
    }
  };

  const handleUploadSuccess = async () => {
    certifications.mutate();
  };

  const handleDeleteCertification = async () => {
    setSubmitting(true);
    try {
      setApiError('');
      await deleteCertification(certification.id);
      certifications.mutate((currentCertifications) => {
        return currentCertifications.filter(
          (cert) => cert.id !== certification.id
        );
      });
    } catch (err) {
      setApiError(err.message);
    }
    setSubmitting(false);
  };

  return (
    <Card className="relative">
      {isShowDelete && (
        <div className="absolute top-[6px] right-[6px] text-destructive">
          {!submitting && <Trash2 onClick={handleDeleteCertification} />}
          {submitting && <Spinner />}
        </div>
      )}
      <CardContent className="p-2">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="mb-2">
              <Input
                name="name"
                label="Certification"
                placeholder={placeholder}
              />
              <div className="p-2 flex justify-end">
                {isDirty && (
                  <SpinnerButton loading={submitting} size="sm">
                    Save
                  </SpinnerButton>
                )}
              </div>
            </div>
          </form>
        </Form>
        <h4>Documents</h4>
        {certification.documents.map((doc) => (
          <Document key={doc.id} doc={doc} onDelete={handleDeleteDocument} />
        ))}
        <div className="flex justify-center py-4">
          <DocumentUploader
            setApiError={setApiError}
            ownerObj={{ certificationId: certification.id }}
            onSuccess={handleUploadSuccess}
            documentType={DOCUMENT_TYPE.CERTIFICATION}
          />
        </div>
        {apiError && <Error error={apiError} />}
      </CardContent>
    </Card>
  );
};

export default Certification;
